import React, { useState } from 'react';
import type { Socket } from 'socket.io-client';

interface AdminPanelWorldTabProps {
  apiBaseUrl: string;
  socket?: Socket | null;
  playerId?: string;
  isBusy: boolean;
  onStateChange: (state: { isBusy: boolean; lastMessage: string | null }) => void;
}

export const AdminPanelWorldTab: React.FC<AdminPanelWorldTabProps> = ({
  apiBaseUrl,
  socket,
  playerId,
  isBusy,
  onStateChange,
}) => {
  const [sanctumGrade, setSanctumGrade] = useState<'A' | 'B' | 'C' | 'D'>('A');
  const [sanctumRoomCount, setSanctumRoomCount] = useState<number>(10);
  const [guestStats, setGuestStats] = useState<any>(null);

  const callAdminEndpoint = async (
    endpoint: string,
    actionName: string,
    data?: Record<string, any>
  ): Promise<void> => {
    onStateChange({ isBusy: true, lastMessage: `${actionName}...` });
    try {
      const res = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data || {}),
      });
      const text: string = await res.text();
      const responseData = (() => {
        try {
          return JSON.parse(text);
        } catch {
          return null;
        }
      })();
      if (!res.ok) {
        const message: string =
          typeof responseData?.error === 'string'
            ? responseData.error
            : `HTTP ${res.status}: ${text.slice(0, 200)}`;
        throw new Error(message);
      }
      onStateChange({
        isBusy: false,
        lastMessage: responseData?.message || `${actionName} completed`,
      });
    } catch (err) {
      onStateChange({
        isBusy: false,
        lastMessage: `Error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const confirmAction = async (message: string): Promise<boolean> => {
    return window.confirm(message);
  };

  const runResetReseedReload = async (): Promise<void> => {
    onStateChange({ isBusy: true, lastMessage: 'Running reset + reseed + reload...' });
    try {
      const res = await fetch(`${apiBaseUrl}/api/admin/dev/full-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const res2 = await fetch(`${apiBaseUrl}/api/admin/dev/reseed-permanent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res2.ok) throw new Error(`HTTP ${res2.status}`);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const res3 = await fetch(`${apiBaseUrl}/api/admin/dev/reload-collisions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res3.ok) throw new Error(`HTTP ${res3.status}`);

      onStateChange({
        isBusy: false,
        lastMessage: 'Reset + Reseed + Collisions completed',
      });
    } catch (err) {
      onStateChange({
        isBusy: false,
        lastMessage: `Error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const loadGuestStats = async (): Promise<void> => {
    onStateChange({ isBusy: true, lastMessage: 'Loading guest stats...' });
    try {
      const res = await fetch(`${apiBaseUrl}/api/admin/dev/stats`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const guestData = data.guests || data;
      setGuestStats(guestData);
      onStateChange({ isBusy: false, lastMessage: `Active guests: ${guestData.activeCount}` });
    } catch (err) {
      onStateChange({
        isBusy: false,
        lastMessage: `Error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Reload Collisions */}
      <button
        type="button"
        onClick={() => callAdminEndpoint('/api/admin/dev/reload-collisions', 'Reload collisions')}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Reload Collisions
      </button>

      {/* Reseed Permanents */}
      <button
        type="button"
        onClick={() => {
          void (async () => {
            if (!(await confirmAction('Reseed permanents'))) return;
            void callAdminEndpoint('/api/admin/dev/reseed-permanent', 'Reseed permanents');
          })();
        }}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Reseed Permanents
      </button>

      {/* Full Reset */}
      <button
        type="button"
        onClick={() => {
          void (async () => {
            if (!(await confirmAction('Full reset'))) return;
            void callAdminEndpoint('/api/admin/dev/full-reset', 'Full reset');
          })();
        }}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#742a2a',
          color: '#fed7d7',
          border: '1px solid #9b2c2c',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Full Reset
      </button>

      {/* Reset + Reseed + Collisions */}
      <button
        type="button"
        onClick={() => {
          void (async () => {
            if (!(await confirmAction('Reset + reseed + collisions'))) return;
            void runResetReseedReload();
          })();
        }}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Reset + Reseed + Collisions
      </button>

      {/* Clear All Caches */}
      <button
        type="button"
        onClick={() => {
          void (async () => {
            if (!(await confirmAction('Clear all client research caches'))) return;
            onStateChange({ isBusy: true, lastMessage: 'Broadcasting cache clear...' });
            try {
              if (!socket) {
                throw new Error('Socket not available');
              }
              socket.emit('admin:clearAllCaches', {}, (result: { success: boolean; message: string }) => {
                onStateChange({ isBusy: false, lastMessage: result?.message ?? 'Cache cleared for all clients' });
              });
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Unknown error';
              onStateChange({ isBusy: false, lastMessage: `Error: ${message}` });
            }
          })();
        }}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#3d5a80',
          color: '#bee3f8',
          border: '1px solid #4c7cb8',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Clear All Caches
      </button>

      {/* Debug World State */}
      <button
        type="button"
        onClick={async () => {
          onStateChange({ isBusy: true, lastMessage: 'Checking world state...' });
          try {
            const res = await fetch(`${apiBaseUrl}/api/admin/dev/debug-world-state`);
            const text: string = await res.text();
            const data = (() => {
              try {
                return JSON.parse(text) as any;
              } catch {
                return null;
              }
            })();
            if (!res.ok) {
              const message: string =
                typeof data?.error === 'string'
                  ? data.error
                  : `HTTP ${res.status}: ${text.slice(0, 200)}`;
              throw new Error(message);
            }
            if (!data) {
              throw new Error(`Invalid JSON response: ${text.slice(0, 200)}`);
            }
            console.log('World State Debug:', data);
            const msg = `Islands: ${data.world?.totalIslands || 0}, Permanent: ${data.world?.permanentIslands || 0}, TradingPosts: ${data.world?.tradingPosts?.length || 0}, Shipyards: ${data.world?.shipyards?.length || 0}, NPCs: ${data.ships?.npcs || 0}`;
            onStateChange({ isBusy: false, lastMessage: msg });
          } catch (err) {
            onStateChange({ isBusy: false, lastMessage: `Error: ${err}` });
          }
        }}
        disabled={isBusy}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#1a365d',
          color: '#90cdf4',
          border: '1px solid #2b6cb0',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Debug World State
      </button>

      {/* Sanctums Section */}
      <div style={{ padding: '6px 6px', border: '1px solid #4a5568', backgroundColor: '#111827', marginTop: 4 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Sanctums</div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          <select
            value={sanctumGrade}
            onChange={(e) => setSanctumGrade(e.target.value as 'A' | 'B' | 'C' | 'D')}
            style={{
              padding: '2px 4px',
              fontSize: 10,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: 'pointer',
            }}
          >
            <option value="A">Grade A</option>
            <option value="B">Grade B</option>
            <option value="C">Grade C</option>
            <option value="D">Grade D</option>
          </select>
          <input
            type="number"
            min="8"
            max="12"
            value={sanctumRoomCount}
            onChange={(e) => setSanctumRoomCount(Math.max(8, Math.min(12, parseInt(e.target.value) || 10)))}
            style={{
              padding: '2px 4px',
              fontSize: 10,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              width: 50,
            }}
            placeholder="Rooms"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            if (!socket) {
              onStateChange({ isBusy: false, lastMessage: 'Socket not available' });
              return;
            }
            onStateChange({ isBusy: true, lastMessage: 'Creating sanctum...' });
            socket.emit('sanctum:create_and_enter', {
              playerId: playerId || socket.id,
              config: {
                grade: sanctumGrade,
                roomCount: sanctumRoomCount,
              },
            });
          }}
          disabled={isBusy}
          style={{
            width: '100%',
            padding: '2px 6px',
            fontSize: 10,
            backgroundColor: '#6b46c1',
            color: '#e9d5ff',
            border: '1px solid #a78bfa',
            borderRadius: 0,
            cursor: isBusy ? 'default' : 'pointer',
          }}
        >
          Create & Enter Sanctum
        </button>
      </div>

      {/* Guest Sessions Section */}
      <div style={{ padding: '6px 6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Guest Sessions</div>
        <button
          type="button"
          onClick={loadGuestStats}
          disabled={isBusy}
          style={{
            padding: '2px 6px',
            fontSize: 10,
            backgroundColor: '#2d3748',
            color: '#edf2f7',
            border: '1px solid #4a5568',
            borderRadius: 0,
            cursor: isBusy ? 'default' : 'pointer',
          }}
        >
          Refresh Guest Stats
        </button>
        {guestStats && (
          <div style={{ marginTop: 6, fontSize: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div>Active: {guestStats.activeCount}</div>
            <div>Lifetime: {guestStats.lifetimeGuests}</div>
            <div>Total Events: {guestStats.totalEvents}</div>
            {guestStats.activeSessions.length > 0 && (
              <div style={{ marginTop: 4, maxHeight: 140, overflowY: 'auto', borderTop: '1px solid #4a5568', paddingTop: 4 }}>
                {guestStats.activeSessions.slice(0, 10).map((s: any) => (
                  <div key={s.socketId} style={{ marginBottom: 3 }}>
                    <div style={{ fontWeight: 600 }}>{s.name || 'Guest'}</div>
                    <div style={{ opacity: 0.8 }}>
                      Events: {s.eventCount} • Last: {new Date(s.lastEventAt).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                {guestStats.activeSessions.length > 10 && (
                  <div style={{ opacity: 0.8 }}>+ {guestStats.activeSessions.length - 10} more…</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Debug Whirlpools */}
      <button
        type="button"
        disabled={isBusy}
        onClick={async () => {
          onStateChange({ isBusy: true, lastMessage: 'Fetching whirlpools...' });
          try {
            const res = await fetch(`${apiBaseUrl}/api/admin/dev/debug-whirlpools`);
            const text: string = await res.text();
            const data = (() => {
              try {
                return JSON.parse(text) as any;
              } catch {
                return null;
              }
            })();
            if (!res.ok) {
              const message: string =
                typeof data?.error === 'string'
                  ? data.error
                  : `HTTP ${res.status}: ${text.slice(0, 200)}`;
              throw new Error(message);
            }
            if (!data) {
              throw new Error(`Invalid JSON response: ${text.slice(0, 200)}`);
            }
            const msg = `Whirlpools: ${data.total || 0}`;
            onStateChange({ isBusy: false, lastMessage: msg });
          } catch (err) {
            onStateChange({ isBusy: false, lastMessage: `Error: ${err}` });
          }
        }}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#1a365d',
          color: '#90cdf4',
          border: '1px solid #2b6cb0',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Debug Whirlpools
      </button>

      {/* Debug Resource Node Counts */}
      <button
        type="button"
        disabled={isBusy}
        onClick={async () => {
          onStateChange({ isBusy: true, lastMessage: 'Fetching resource nodes...' });
          try {
            const res = await fetch(`${apiBaseUrl}/api/admin/dev/debug-resource-nodes`);
            const text: string = await res.text();
            const data = (() => {
              try {
                return JSON.parse(text) as any;
              } catch {
                return null;
              }
            })();
            if (!res.ok) {
              const message: string =
                typeof data?.error === 'string'
                  ? data.error
                  : `HTTP ${res.status}: ${text.slice(0, 200)}`;
              throw new Error(message);
            }
            if (!data) {
              throw new Error(`Invalid JSON response: ${text.slice(0, 200)}`);
            }
            const msg = `Resource Nodes: ${data.totalActive || 0}`;
            onStateChange({ isBusy: false, lastMessage: msg });
          } catch (err) {
            onStateChange({ isBusy: false, lastMessage: `Error: ${err}` });
          }
        }}
        style={{
          padding: '2px 6px',
          fontSize: 10,
          backgroundColor: '#2d3748',
          color: '#edf2f7',
          border: '1px solid #4a5568',
          borderRadius: 0,
          cursor: isBusy ? 'default' : 'pointer',
        }}
      >
        Debug Resource Node Counts
      </button>
    </div>
  );
};
